import { useRef, useEffect, useState } from 'react';
import * as THREE from 'three';

const ORANGE = 0xff7a3d;
const TEAL = 0x00e5c1;

function CssFallback() {
  return (
    <div className="relative w-full h-full flex items-center justify-center" data-testid="cyber-shield-fallback">
      <div className="absolute w-56 h-56 rounded-full border border-[#FF7A3D]/30 animate-[spin_18s_linear_infinite]" />
      <div className="absolute w-72 h-72 rounded-full border border-[#00E5C1]/20 animate-[spin_26s_linear_infinite_reverse]" />
      <div className="w-28 h-28 rounded-full bg-gradient-to-br from-[#FF7A3D] to-[#00E5C1] blur-xl opacity-40" />
      <div className="absolute w-16 h-16 rounded-full bg-[#FF7A3D]/60 blur-md" />
    </div>
  );
}

export default function CyberShield3D() {
  const mountRef = useRef(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    let renderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true, powerPreference: 'high-performance' });
    } catch (e) {
      setFailed(true);
      return;
    }

    let width = mount.clientWidth || 1;
    let height = mount.clientHeight || 1;

    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 100);
    camera.position.set(0, 0, 7);

    // Lighting
    scene.add(new THREE.AmbientLight(0x1a1a22, 0.9));
    const pLight = new THREE.PointLight(ORANGE, 2.6, 100);
    pLight.position.set(4, 3, 5);
    scene.add(pLight);
    const dLight = new THREE.DirectionalLight(TEAL, 2.2);
    dLight.position.set(-5, -2, -3);
    scene.add(dLight);

    const root = new THREE.Group();
    scene.add(root);

    // Inner wireframe core (orange)
    const inner = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.35, 1),
      new THREE.MeshStandardMaterial({ color: ORANGE, emissive: ORANGE, emissiveIntensity: 0.55, wireframe: true, roughness: 0.3, metalness: 0.6 })
    );
    root.add(inner);

    // Solid glowing nucleus
    const nucleus = new THREE.Mesh(
      new THREE.IcosahedronGeometry(1.0, 0),
      new THREE.MeshStandardMaterial({ color: ORANGE, emissive: ORANGE, emissiveIntensity: 0.9, roughness: 0.1, metalness: 0.8, transparent: true, opacity: 0.16 })
    );
    root.add(nucleus);

    // Outer shield (teal wireframe)
    const outer = new THREE.Mesh(
      new THREE.IcosahedronGeometry(2.25, 1),
      new THREE.MeshBasicMaterial({ color: TEAL, wireframe: true, transparent: true, opacity: 0.22 })
    );
    root.add(outer);

    // Orbit rings with node satellites
    const makeRing = (radius, count, color, tilt) => {
      const g = new THREE.Group();
      g.rotation.x = tilt;
      const nodeGeo = new THREE.SphereGeometry(0.055, 10, 10);
      const nodeMat = new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 1.6 });
      for (let i = 0; i < count; i++) {
        const a = (i / count) * Math.PI * 2;
        const m = new THREE.Mesh(nodeGeo, nodeMat);
        m.position.set(Math.cos(a) * radius, 0, Math.sin(a) * radius);
        g.add(m);
      }
      const torus = new THREE.Mesh(
        new THREE.TorusGeometry(radius, 0.004, 8, 90),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.28 })
      );
      torus.rotation.x = Math.PI / 2;
      g.add(torus);
      root.add(g);
      return g;
    };
    const ring1 = makeRing(2.9, 6, TEAL, 0.5);
    const ring2 = makeRing(3.6, 4, ORANGE, -0.35);

    // Particle field
    const PCOUNT = 320;
    const positions = new Float32Array(PCOUNT * 3);
    for (let i = 0; i < PCOUNT; i++) {
      const r = 3.2 + Math.random() * 4.5;
      const theta = Math.random() * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      positions[i * 3] = r * Math.sin(phi) * Math.cos(theta);
      positions[i * 3 + 1] = r * Math.sin(phi) * Math.sin(theta);
      positions[i * 3 + 2] = r * Math.cos(phi);
    }
    const pGeo = new THREE.BufferGeometry();
    pGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    const particles = new THREE.Points(
      pGeo,
      new THREE.PointsMaterial({ size: 0.035, color: TEAL, transparent: true, opacity: 0.55, sizeAttenuation: true })
    );
    root.add(particles);

    // Pointer parallax
    const pointer = { x: 0, y: 0 };
    const onMove = (e) => {
      const rect = mount.getBoundingClientRect();
      pointer.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      pointer.y = ((e.clientY - rect.top) / rect.height) * 2 - 1;
    };
    window.addEventListener('pointermove', onMove);

    // Resize
    const resize = () => {
      width = mount.clientWidth || 1;
      height = mount.clientHeight || 1;
      renderer.setSize(width, height);
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(mount);

    let raf;
    const clock = new THREE.Clock();
    const animate = () => {
      raf = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (!reduced) {
        inner.rotation.y += delta * 0.25;
        inner.rotation.x += delta * 0.08;
        outer.rotation.y -= delta * 0.12;
        outer.rotation.z += delta * 0.05;
        ring1.rotation.y += delta * 0.35;
        ring2.rotation.y -= delta * 0.22;
        particles.rotation.y += delta * 0.04;
        root.rotation.y += (pointer.x * 0.4 - root.rotation.y) * 0.04;
        root.rotation.x += (-pointer.y * 0.3 - root.rotation.x) * 0.04;
      }
      renderer.render(scene, camera);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointermove', onMove);
      ro.disconnect();
      scene.traverse((obj) => {
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) {
          if (Array.isArray(obj.material)) obj.material.forEach((m) => m.dispose());
          else obj.material.dispose();
        }
      });
      renderer.dispose();
      if (renderer.domElement && renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
  }, []);

  if (failed) return <CssFallback />;

  return <div ref={mountRef} className="w-full h-full" data-testid="cyber-shield-3d" />;
}
